<template>
  <div class="main-view-with-app-bar">
    <v-app-bar app color="surface" :elevation="0" class="cv-app-bar">
      <v-app-bar-nav-icon
        variant="text"
        aria-label="Menü"
        @click.stop="drawer = !drawer"
      ></v-app-bar-nav-icon>

      <router-link :to="homePath" class="cv-logo-link ml-1">
        <img :src="logo" height="34" alt="Cégvándor" />
      </router-link>

      <v-chip
        v-if="portalLabel"
        size="small"
        variant="tonal"
        color="primary"
        class="ml-3 d-none d-sm-flex"
        label
      >
        {{ portalLabel }}
      </v-chip>

      <v-spacer></v-spacer>

      <NotificationBell class="mr-1" />

      <span class="cv-user-name d-none d-sm-inline mr-2">{{ fullName }}</span>
      <v-btn
        icon="mdi-logout"
        variant="text"
        aria-label="Kijelentkezés"
        @click="logout"
      ></v-btn>
    </v-app-bar>

    <v-navigation-drawer
      v-model="drawer"
      :location="$vuetify.display.mobile ? 'bottom' : undefined"
      temporary
      class="cv-drawer"
    >
      <div class="pa-4">
        <img :src="logo" height="30" alt="Cégvándor" />
      </div>
      <v-divider></v-divider>

      <v-list nav density="comfortable" class="pa-2">
        <v-list-item
          v-for="route in routes"
          :key="route.path"
          :prepend-icon="route.icon"
          :active="isActive(route.path)"
          active-color="primary"
          rounded="lg"
          @click="navigateTo(route.path)"
        >
          <v-list-item-title>{{ route.name }}</v-list-item-title>
        </v-list-item>
      </v-list>

      <template #append>
        <v-divider></v-divider>
        <div class="pa-4">
          <div class="cv-eyebrow mb-1">Bejelentkezve</div>
          <div class="text-body-2 font-weight-medium">{{ fullName }}</div>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- One place to own page gutters, so individual views stop inventing them. -->
    <div class="cv-page-body">
      <!--
        Keyed by path so a param-only navigation remounts the view. Every detail view
        loads its data once in setup() from the route param, so without this, going from
        /bookings/A to /bookings/B reuses the instance and leaves the old booking on
        screen under the new URL.
      -->
      <router-view :key="route.path"></router-view>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import logo from "@/assets/logotran2.png";
import NotificationBell from "@/components/layout/NotificationBell.vue";
import { useNotifications } from "@/services/useNotifications";

export type ShellRoute = {
  path: string;
  name: string;
  icon: string;
};

defineProps<{
  routes: ShellRoute[];
  homePath: string;
  fullName: string;
  portalLabel?: string;
}>();

const emit = defineEmits<{ (e: "logout"): void }>();

const drawer = ref(false);
const router = useRouter();
const route = useRoute();

// The shell is the only app bar in the app and both portals wrap it, so opening the
// stream here covers admin and customer alike. By the time it mounts the router guard has
// already established who the user is.
const { connect, disconnect } = useNotifications();
onMounted(() => connect());
onUnmounted(() => disconnect());

/** Exact match only: every path is a prefix of the deeper ones under it. */
const isActive = (path: string) => route.path === path;

function navigateTo(routePath: string) {
  router.push(routePath === "" ? "/" : routePath);
  drawer.value = false;
}

function logout() {
  emit("logout");
}
</script>

<style scoped>
.cv-app-bar {
  border-bottom: 1px solid var(--cv-border-neutral);
}

.cv-logo-link {
  display: flex;
  align-items: center;
}

.cv-user-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--cv-text-muted);
}

.cv-drawer :deep(.v-list-item--active) {
  background: var(--cv-brand-tint);
  color: var(--cv-brand);
  font-weight: 600;
}
</style>
